/**
 * OEProvidersWrapper — wraps the FinTech Ops context providers with an
 * ErrorBoundary so that provider-level errors (thrown during render or in
 * effects that surface synchronously) are caught here instead of bubbling
 * to the global ErrorBoundary and killing the entire app shell.
 *
 * On error the providers are unmounted but AppRouter still renders, so the
 * user keeps side navigation and can navigate away from the broken page.
 */
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CostMetricsProvider } from './context/CostMetricsProvider';
import { DashboardFilterSync } from './context/DashboardFilterSync';
import { DogmaProvider } from './context/DogmaProvider';
import { NotificationProvider } from './context/NotificationProvider';
import { PolicyEngineProvider } from './context/PolicyEngineProvider';
import { RiskRemediatorProvider } from './context/RiskRemediatorProvider';
import { SasProvider } from './context/SasProvider';
import { ShepherdRisksProvider } from './context/ShepherdRisksProvider';
import { SharedFiltersProvider } from './context/SharedFiltersProvider';
import { TicketsProvider } from './context/TicketsProvider';
import { UpgradeMedicProvider } from './context/UpgradeMedicProvider';
import { UserMetricsProvider } from './context/UserMetricsProvider';
import { useWarmUserMetricsWorkers } from './hooks/useWarmUserMetricsWorkers';
import { logger } from 'src/logger';

const OEProvidersFallback: React.FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;

interface Props {
  children: React.ReactNode;
}

export const OEProvidersWrapper: React.FC<Props> = ({ children }) => {
  // Warm the User Metrics worker chunk here so it downloads alongside the provider S3
  // fetches rather than after the User Metrics page renders. See the hook for why this
  // seam and not UserMetricsProvider.
  useWarmUserMetricsWorkers();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => {
        logger.error('OE providers crashed, rendering without OE context', error);
        return <OEProvidersFallback>{children}</OEProvidersFallback>;
      }}
    >
      <SharedFiltersProvider>
        <NotificationProvider>
          <CostMetricsProvider>
            <DogmaProvider>
              <PolicyEngineProvider>
                <SasProvider>
                  <TicketsProvider>
                    <UserMetricsProvider>
                      <RiskRemediatorProvider>
                        <ShepherdRisksProvider>
                          <UpgradeMedicProvider>
                            <DashboardFilterSync />
                            {children}
                          </UpgradeMedicProvider>
                        </ShepherdRisksProvider>
                      </RiskRemediatorProvider>
                    </UserMetricsProvider>
                  </TicketsProvider>
                </SasProvider>
              </PolicyEngineProvider>
            </DogmaProvider>
          </CostMetricsProvider>
        </NotificationProvider>
      </SharedFiltersProvider>
    </ErrorBoundary>
  );
};
